import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import type { PerformanceLoanDetail } from './types';
import { formatCurrency, formatDateKey } from './formatters';

interface PerformanceLoanSheetProps {
  open: boolean;
  title: string;
  description: string;
  loans: PerformanceLoanDetail[];
  onOpenChange: (open: boolean) => void;
}

type SortKey = 'eventDate' | 'amount' | 'loanNumber' | 'borrower' | 'loanOfficer';

const PAGE_SIZE = 25;

function compareLoans(a: PerformanceLoanDetail, b: PerformanceLoanDetail, sort: SortKey): number {
  if (sort === 'amount') {
    return b.amount - a.amount;
  }
  if (sort === 'loanNumber') {
    return a.loanNumber.localeCompare(b.loanNumber, undefined, { numeric: true });
  }
  if (sort === 'borrower') {
    return a.borrower.localeCompare(b.borrower);
  }
  if (sort === 'loanOfficer') {
    return a.loanOfficer.localeCompare(b.loanOfficer);
  }
  return b.eventDate.localeCompare(a.eventDate);
}

export function PerformanceLoanSheet({
  open,
  title,
  description,
  loans,
  onOpenChange
}: PerformanceLoanSheetProps) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('eventDate');
  const [page, setPage] = useState(0);

  const filteredLoans = useMemo(() => {
    const query = search.trim().toLowerCase();
    return loans
      .filter((loan) => {
        if (!query) {
          return true;
        }
        const searchable = [
          loan.loanNumber,
          loan.borrower,
          loan.loanOfficer,
          loan.folder,
          loan.channel,
          loan.investor || '',
          loan.product || ''
        ].join(' ').toLowerCase();
        return searchable.includes(query);
      })
      .sort((a, b) => compareLoans(a, b, sort));
  }, [loans, search, sort]);

  useEffect(() => {
    setPage(0);
  }, [search, sort, loans]);

  const pageCount = Math.max(1, Math.ceil(filteredLoans.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageLoans = filteredLoans.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex h-full w-full max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl">
        <SheetHeader className="border-b px-5 py-5 pr-12 sm:px-6">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            {description} · {loans.length.toLocaleString()} loan{loans.length === 1 ? '' : 's'}
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-2 border-b px-5 py-3 sm:grid-cols-[minmax(0,1fr)_180px] sm:px-6">
          <label className="relative">
            <span className="sr-only">Search contributing loans</span>
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search loan, borrower, LO…"
              className="pl-9"
            />
          </label>
          <Select value={sort} onValueChange={(value) => setSort(value as SortKey)}>
            <SelectTrigger aria-label="Sort contributing loans">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="eventDate">Newest event</SelectItem>
              <SelectItem value="amount">Largest amount</SelectItem>
              <SelectItem value="loanNumber">Loan number</SelectItem>
              <SelectItem value="borrower">Borrower</SelectItem>
              <SelectItem value="loanOfficer">Loan officer</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-2 px-4 py-3 md:hidden">
            {pageLoans.map((loan) => (
              <div key={`${loan.id}-${loan.eventDate}`} className="rounded-lg border bg-card p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{loan.borrower || '—'}</div>
                    <div className="text-xs text-muted-foreground">Loan {loan.loanNumber || '—'} · {loan.loanOfficer || 'Unassigned'}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium tabular-nums">{formatCurrency(loan.amount)}</div>
                    <div className="text-xs text-muted-foreground">{formatDateKey(loan.eventDate)}</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <Badge variant="secondary">{loan.folder || 'Unknown'}</Badge>
                  <span>{loan.channel || '—'}</span>
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">
                  Created {formatDateKey(loan.createdDate)} · Application {formatDateKey(loan.applicationDate)}
                </div>
              </div>
            ))}
            {pageLoans.length === 0 && (
              <div className="py-12 text-center text-sm text-muted-foreground">No loans match this search.</div>
            )}
          </div>
          <div className="hidden min-w-[920px] px-5 py-3 md:block sm:px-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Loans contributing to {title}</caption>
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-2 py-2 font-medium">Loan #</th>
                  <th className="px-2 py-2 font-medium">Borrower</th>
                  <th className="px-2 py-2 font-medium">Loan officer</th>
                  <th className="px-2 py-2 font-medium">Event date</th>
                  <th className="px-2 py-2 text-right font-medium">Amount</th>
                  <th className="px-2 py-2 font-medium">Folder / status</th>
                  <th className="px-2 py-2 font-medium">Channel</th>
                </tr>
              </thead>
              <tbody>
                {pageLoans.map((loan) => (
                  <tr key={`${loan.id}-${loan.eventDate}`} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-2 py-3 font-medium tabular-nums">{loan.loanNumber || '—'}</td>
                    <td className="px-2 py-3">{loan.borrower || '—'}</td>
                    <td className="px-2 py-3">{loan.loanOfficer || 'Unassigned'}</td>
                    <td className="px-2 py-3 tabular-nums">
                      <div className="font-medium">{formatDateKey(loan.eventDate)}</div>
                      <div className="mt-0.5 whitespace-nowrap text-[11px] text-muted-foreground">
                        Created {formatDateKey(loan.createdDate)} · App {formatDateKey(loan.applicationDate)}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-right tabular-nums">{formatCurrency(loan.amount)}</td>
                    <td className="px-2 py-3"><Badge variant="secondary">{loan.folder || 'Unknown'}</Badge></td>
                    <td className="px-2 py-3">{loan.channel || '—'}</td>
                  </tr>
                ))}
                {pageLoans.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-2 py-12 text-center text-muted-foreground">
                      No loans match this search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <ScrollBar orientation="horizontal" className="hidden md:flex" />
        </ScrollArea>

        <div className="flex items-center justify-between gap-3 border-t px-5 py-3 text-sm sm:px-6">
          <span className="text-muted-foreground">
            {filteredLoans.length === 0
              ? '0 results'
              : `${currentPage * PAGE_SIZE + 1}–${Math.min((currentPage + 1) * PAGE_SIZE, filteredLoans.length)} of ${filteredLoans.length}`}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              disabled={currentPage === 0}
              onClick={() => setPage((value) => Math.max(0, value - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="sr-only">Previous page</span>
            </Button>
            <span className="min-w-20 text-center text-xs tabular-nums">
              Page {currentPage + 1} of {pageCount}
            </span>
            <Button
              variant="outline"
              size="icon"
              disabled={currentPage >= pageCount - 1}
              onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))}
            >
              <ChevronRight className="h-4 w-4" />
              <span className="sr-only">Next page</span>
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
